import { ChevronDownIcon, XCircleFillIcon } from '@primer/octicons-react';
import { Box, Button, IconButton, SelectPanel } from '@primer/react';
import { useEffect, useMemo, useState } from 'react';
import { crmSurveyFormQuestionApi } from '../../api/crmSurveyFormQuestionApi';
import { ICrmSurveyFormQuestionDataSource } from '../../models/response/crm-survey/ICrmSurveyFormQuestionDataSource';

type CrmSurveyComboBoxProps = {
    crm_survey_form_question_id: number;
    value?: number;
    onValueChanged: (id: number, data?: ICrmSurveyFormQuestionDataSource) => void;
    width?: string | number;
    dm_coso_id?: number;
    stylingMode?: 'outlined' | 'filled' | 'underlined';
    isShowClearButton?: boolean,
    fieldValue?: number;
    align?: "left" | "right";
    excludedOptionIds?: string;
};

type ComboBoxItem = ICrmSurveyFormQuestionDataSource & { rawId: string };

const CrmSurveyComboBox: React.FC<CrmSurveyComboBoxProps> = (props) => {
    const [rawDataSource, setRawDataSource] = useState<ComboBoxItem[]>([]);
    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState('');

    const dataSource = useMemo(() => {
        if (!props.excludedOptionIds) {
            return rawDataSource;
        }
        const excludedSet = new Set(
            props.excludedOptionIds
                .split(',')
                .map(s => s.trim().toLowerCase())
                .filter(Boolean)
        );
        if (excludedSet.size === 0) {
            return rawDataSource;
        }
        return rawDataSource.filter(item => !excludedSet.has(item.rawId.toLowerCase()));
    }, [rawDataSource, props.excludedOptionIds]);

    const filteredItems = useMemo(() => {
        const safeData = Array.isArray(dataSource) ? dataSource : [];
        return safeData.filter(item => item.text?.toLowerCase().includes(filter.toLowerCase()));
    }, [dataSource, filter]);

    //nếu selected= obj -> chọn 1, = array -> chọn nhiều
    const selected = useMemo(() => {
        const safeData = Array.isArray(dataSource) ? dataSource : [];
        return safeData.find(x => x.id == props.value);
    }, [dataSource, props.value]);

    const setSelected = (selecteds: any) => {
        if (selecteds)
            props.onValueChanged(selecteds.id, selecteds);
    };

    useEffect(() => {
        handleReload();
    }, [props.crm_survey_form_question_id, props.fieldValue, props.dm_coso_id]);

    const handleReload = async () => {
        const shouldIncludeExcluded = props.excludedOptionIds !== undefined;
        const res = await crmSurveyFormQuestionApi.select_datasource(
            props.crm_survey_form_question_id,
            props.dm_coso_id ?? 1,
            props.fieldValue ?? 0,
            shouldIncludeExcluded
        );
        if (res.is_success) {
            const safe = Array.isArray(res.data) ? res.data : [];
            // Chuyển đổi id từ string sang number để phù hợp với interface
            const convertedData: ComboBoxItem[] = safe.map((item: any) => ({
                ...item,
                rawId: String(item.id ?? '').trim(),
                id: parseInt(item.id, 10)
            }));
            setRawDataSource(convertedData);
        } else {
            setRawDataSource([]);
        }
    };

    return (
        <SelectPanel
            renderAnchor={({ children, 'aria-labelledby': ariaLabelledBy, ...anchorProps }) => (
                <Box sx={{ display: "flex" }}>
                    <Button
                        sx={{
                            width: "-webkit-fill-available",
                        }}
                        trailingAction={ChevronDownIcon}
                        aria-labelledby={`${ariaLabelledBy}`}
                        {...anchorProps}
                        className={props.align ? `button-${props.align}` : ''}
                    >
                        <p
                            style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {(selected && selected.text) ? selected.text : 'Chọn'}
                        </p>
                    </Button>
                </Box>
            )}
            title={<Box sx={{ display: "flex", flexDirection: "row-reverse" }}>
                {props.isShowClearButton && props.value !== undefined && props.value > 0 &&
                    <IconButton aria-label={"Clear"} icon={XCircleFillIcon}
                        variant="invisible"
                        onClick={() => {
                            props.onValueChanged(0, undefined);
                        }}
                    />
                }
            </Box>}
            placeholderText="Search"
            open={open}
            onOpenChange={setOpen}
            items={filteredItems}
            selected={selected}
            onSelectedChange={setSelected}
            onFilterChange={setFilter}
            showItemDividers={true}
            overlayProps={{ width: 'medium', height: 'medium' }}
        />
    );
};

export default CrmSurveyComboBox;
